import * as AppStorage from "@/base/async-storage"
import { handleReloadApp } from "@/base/utility"
import { useErrorHandler } from "@/hooks/useErrorHandler"
import { getIsDemoModeActiveFromState } from "@/redux/selectors"
import { useRootDispatch } from "@/redux/useRootDispatch"
import { useRootSelector } from "@/redux/useRootSelector"
import { COLORS, IconProp, RADII, SPACING, getFontName } from "@/theme"
import { renderIconFromIconProp } from "@/theme/icons/renderIconFromIconProp"
import { SHADOWS } from "@/theme/theme/shadows"
import React from "react"
import { StyleSheet, Pressable, View, Text } from "react-native"
import i18n from "@/translations/i18n"

interface DemoModeIndicatorProps {
    IconComponent: IconProp
}

const TEXT_COLOR = COLORS.$textDark.primary

// Fixed frame of the floating "Exit Demo" pill, exported so overlays that
// share the top-right edge (e.g. the status-map heatmap/activity toggles)
// can place themselves below it while demo mode is active. The pill is
// positioned in the dashboard root frame, not the safe-area frame.
export const DEMO_MODE_INDICATOR_TOP = SPACING.$28
export const DEMO_MODE_INDICATOR_HEIGHT = 32
export const DEMO_MODE_INDICATOR_BOTTOM =
    DEMO_MODE_INDICATOR_TOP + DEMO_MODE_INDICATOR_HEIGHT

const styles = StyleSheet.create({
    contentContainer: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "center",
    },
    root: {
        alignItems: "center",
        backgroundColor: COLORS.$primary[500],
        borderRadius: RADII.$full,
        top: DEMO_MODE_INDICATOR_TOP,
        right: SPACING.$5,
        justifyContent: "center",
        paddingHorizontal: SPACING.$4,
        position: "absolute",
        ...SHADOWS.$8,
    },
    text: {
        color: TEXT_COLOR,
        fontFamily: getFontName("Poppins_500Medium"),
        fontSize: 16,
        marginLeft: 8,
        textTransform: "uppercase", // Adds space between icon and label
    },
    iconContainer: {
        marginRight: 8, // Adds space between icon and text
    },
})

function DemoModeIndicator({ IconComponent }: DemoModeIndicatorProps) {
    const demoModeIsActive = useRootSelector(getIsDemoModeActiveFromState)
    const dispatch = useRootDispatch()
    const handleError = useErrorHandler()

    const onPress = () => {
        dispatch(AppStorage.exitDemoMode())
            .unwrap()
            .then(handleReloadApp)
            .catch((error) =>
            handleError(error, { toastMessage: "default" }),
            )
    }

    if (demoModeIsActive) {
        return (
            <Pressable
                onPress={onPress}
                style={({ pressed }) => [
                styles.root,
                {
                    height: DEMO_MODE_INDICATOR_HEIGHT,
                    opacity: pressed ? 0.5 : 1,
                },
                ]}
            >
                <View style={styles.contentContainer}>
                {Boolean(IconComponent) && (
                    <View style={styles.iconContainer}>
                    {renderIconFromIconProp(IconComponent, {
                        color: TEXT_COLOR,
                    })}
                    </View>
                )}
                <Text style={styles.text}>{i18n.t("demoMode:exitDemoButton")}</Text>
                </View>
            </Pressable>
        )
    }

    return null
}

export default DemoModeIndicator